import React, { forwardRef, useState, useMemo, useRef, useEffect } from 'react';
import {
  Typography,
  useTheme,
  Menu,
  MenuItem,
  FormControl,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Box,
  IconButton
} from '@mui/material';
import { CheckCircle, Error, Search, Close, Check } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomIconButton from './CustomIconButton';
import add from 'packages/novous_web/src/assets/icons/plusBlue.svg';
import useDebounce from '../../hooks/useDebounce';
import Checkbox from '@mui/material/Checkbox';
import searchIcon from 'packages/novous_web/src/assets/icons/search.svg';

interface CustomIconButtonOptions {
  text: string;
  onClick: () => void;
}

interface CustomMultiSelectDropDownProps {
  label?: string;
  name?: string;
  options: { value: string | number; label: string }[];
  value: (string | number)[];
  onChange: (selectedValues: (string | number)[], isAllSelected?: boolean) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isCustomIconButton?: boolean;
  customIconLabel?: string;
  isMandatory?: boolean;
  selectAll?: boolean;
  status?: 'success' | 'error';
  customIconButton?: CustomIconButtonOptions;
  showDropdownArrow?: boolean;
  maxDisplayedChips?: number;
  icon?: boolean;
}

const CustomMultiSelectDropDown: React.FC<CustomMultiSelectDropDownProps> = ({
  label,
  name,
  options,
  value = [],
  isCustomIconButton = false,
  onChange,
  customIconLabel = '',
  isMandatory = false,
  selectAll = false,
  placeholder = 'Select options',
  isDisabled = false,
  customIconButton,
  status,
  showDropdownArrow = false,
  maxDisplayedChips = 3,
  icon,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isAllSelected = value.length === options.length;
  const debouncedQuery = useDebounce(searchQuery, 300);

  const filteredOptions = useMemo(() => {
    const query = debouncedQuery.toLowerCase().trim();
    if (!query) return options;
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, debouncedQuery]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement | HTMLButtonElement>) => {
    if (!isDisabled) setAnchorEl(event.currentTarget);
  };

  const handleSelectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isAllSelected) {
      onChange([options[0].value], isAllSelected);
    } else {
      onChange(options.map((o) => o.value));
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleSelect = (selectedValue: string | number) => {
    const newValue = value.includes(selectedValue)
      ? value.filter((v) => v !== selectedValue)
      : [...value, selectedValue];
    onChange(newValue, isAllSelected);
  };

  const handleRemove = (valueToRemove: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(
      value.filter((v) => v !== valueToRemove),
      isAllSelected,
    );
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([], isAllSelected);
  };

  useEffect(() => {
    if (anchorEl && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [anchorEl, filteredOptions.length]);

  const getIcon = () => {
    if (value.length === 0) return null;
    if (status === 'success') return <CheckCircle sx={{ color: 'green', fontSize: 20 }} />;
    if (status === 'error') return <Error sx={{ color: 'red', fontSize: 20 }} />;
    return null;
  };

  const selectedOptions = options.filter((o) => value.includes(o.value));
  const displayChips = maxDisplayedChips ? selectedOptions.slice(0, maxDisplayedChips) : selectedOptions;
  const moreCount =
    maxDisplayedChips && selectedOptions.length > maxDisplayedChips
      ? selectedOptions.length - maxDisplayedChips
      : 0;

  return (
    <div className="flex flex-col w-full gap-1">
      <div className="flex justify-between items-center gap-2">
        <div className="flex justify-between gap-2 items-center w-full">
          {label && (
            <div className="flex gap-1">
              <Typography variant="body2" fontWeight={600} color="textPrimary">
                {label}
              </Typography>
              {isMandatory && <span className="text-red-500">*</span>}
            </div>
          )}
          {customIconButton && (
            <CustomIconButton
              icon={<img src={add} alt="add" width={16} height={16} />}
              text={customIconButton.text}
              background="transparent"
              outline={false}
              padding="0px"
              size={16}
              height="24px"
              variant="outlined"
              onClick={customIconButton.onClick}
            />
          )}
        </div>
        {isCustomIconButton && (
          <CustomIconButton
            icon={<img src={add} alt="add" width={16} height={16} />}
            text={customIconLabel}
            background="transparent"
            outline={false}
            padding="0px"
            height="24px"
            variant="outlined"
            onClick={() => console.log('Button clicked')}
          />
        )}
      </div>

      <div className="relative">
        <FormControl fullWidth>
          {/* Replacing Button with Box to prevent click open */}
          <Box
            sx={{
              borderRadius: '8px',
              border: `1px solid ${theme.palette.action.selected}`,
              padding: '10px 16px',
              minHeight: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: isDisabled ? theme.palette.action.disabledBackground : 'white',
              color: selectedOptions.length > 0 ? 'black' : theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
             {icon && <img src={searchIcon} alt="search-icon" width={16} height={16} className='mr-2' />}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                flexGrow: 1,
                justifyContent: 'flex-start',
                maxWidth: 'calc(100% - 50px)',
              }}
            >
              {selectedOptions.length === 0 ? (
                <Typography variant="body2" fontWeight={400}>
                  {placeholder}
                </Typography>
              ) : (
                <>
                  {displayChips.map((option) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      size="small"
                      onDelete={(e) => handleRemove(option.value, e)}
                      deleteIcon={<Close fontSize="small" />}
                      sx={{
                        backgroundColor: theme.palette.action.selected,
                        maxWidth: '200px',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  ))}
                  {moreCount > 0 && (
                    <Chip
                      label={`+${moreCount} more`}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.action.selected,
                      }}
                    />
                  )}
                </>
              )}
            </Box>
            {getIcon()}
            {/* + Icon that opens dropdown */}
            <IconButton
              onClick={handleOpen}
              disabled={isDisabled}
              size="small"
              sx={{
                color: '#5547DD',
                '&:hover': { backgroundColor: theme.palette.action.hover }
              }}
            >
              <img src={add} alt="add" width={20} height={20} />
            </IconButton>
          </Box>
        </FormControl>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          disableAutoFocus
          disableEnforceFocus
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          MenuListProps={{
            autoFocus: false,
            autoFocusItem: false,
            disableListWrap: true,
            onBlur: (e) => e.stopPropagation(),
          }}
          PaperProps={{
            sx: {
              borderRadius: '8px',
              maxHeight: '300px',
              minWidth: '200px',
              maxWidth: '400px',
              width: 'auto',
              padding: '0px',
              overflowY: 'auto',
              boxShadow: '0 6px 10px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE and Edge
              '&::-webkit-scrollbar': {
                display: 'none', // Chrome, Safari, Opera
              },
            },
          }}
        >
          <div style={{ padding: '8px 12px', flexShrink: 0 }}>
            <TextField
              fullWidth
              autoFocus
              inputRef={searchInputRef}
              placeholder="Search..."
              size="small"
              variant="outlined"
              value={searchQuery}
              name={name}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: '6px',
                },
              }}
              onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
            />
          </div>
          {selectAll && (
            <MenuItem
              onClick={handleSelectAll}
              sx={{
                padding: '8px 16px',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
              >
                <Typography variant="body2" fontWeight={500} color="secondary">
                  {isAllSelected ? 'Unselect All' : 'Select All'}
                </Typography>
                {isAllSelected && <Check color="primary" fontSize="small" />}
              </Box>
            </MenuItem>
          )}

          {selectedOptions.length > 0 && (
            <MenuItem
              onClick={handleClearAll}
              sx={{
                padding: '8px 16px',
                color: theme.palette.error.main,
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Typography variant="body2" fontWeight={400}>
                Clear all
              </Typography>
            </MenuItem>
          )}

          <Box
            sx={{
              maxHeight: '250px',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
{filteredOptions.map((option) => (
  <MenuItem
    key={option.value}
    onClick={() => handleSelect(option.value)}
    sx={{
      padding: '12px 16px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
      }}
    >
      <Checkbox
        checked={value.includes(option.value)}
        sx={{ p: 0, mr: 1 }}
        color="primary"
        tabIndex={-1}
        disableRipple
      />
      <Typography
        variant="body2"
        fontWeight={400}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          minWidth: 0,
        }}
      >
        {option.label}
      </Typography>
    </Box>
  </MenuItem>
))}
            {filteredOptions.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" color="textSecondary">
                  No results found
                </Typography>
              </MenuItem>
            )}
          </Box>
        </Menu>
      </div>
    </div>
  );
};

export default CustomMultiSelectDropDown;
